"use client";

import { BrandMark, BrandMascot } from "@/components/brand";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";

export type GuestDemoChoiceDismissMethod = "backdrop" | "close_icon" | "escape" | "not_now";

interface GuestDemoChoiceGateProps {
  onChooseFree: () => void;
  onChooseTrial: () => void;
  onDismiss: (method: GuestDemoChoiceDismissMethod) => void;
}

export function GuestDemoChoiceGate({
  onChooseFree,
  onChooseTrial,
  onDismiss,
}: GuestDemoChoiceGateProps) {
  const primaryActionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaryActionRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss("escape");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onDismiss("backdrop");
      }}
    >
      <div
        aria-labelledby="guest-demo-choice-title"
        aria-modal="true"
        className="max-h-[calc(100dvh-2rem)] w-full max-w-[720px] overflow-y-auto rounded-lg border border-white/10 bg-[#121318] text-white shadow-2xl shadow-black/50"
        role="dialog"
      >
        <div className="grid sm:grid-cols-[280px_minmax(0,1fr)]">
          <div className="hidden items-end overflow-hidden border-r border-white/10 bg-white/[0.025] px-0 pt-5 sm:flex">
            <BrandMascot
              pose="signature"
              className="h-auto w-[280px] object-contain object-bottom"
              sizes="280px"
            />
          </div>

          <div className="p-6">
            <div className="mb-4 sm:hidden">
              <BrandMark size={56} className="h-14 w-14 object-contain" alt="" />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-300">
                  Five-minute practice complete
                </p>
                <h2
                  id="guest-demo-choice-title"
                  className="mt-2 text-2xl font-semibold leading-tight"
                >
                  Great conversation. You spoke for five minutes.
                </h2>
              </div>
              <button
                aria-label="Close and finish for now"
                className="rounded-md p-1 text-neutral-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
                onClick={() => onDismiss("close_icon")}
                type="button"
              >
                <X size={22} />
              </button>
            </div>

            <p className="mt-4 text-sm leading-6 text-neutral-300">
              Keep speaking without limits, or create a free account to continue with today&apos;s
              free practice.
            </p>

            <div className="mt-6 space-y-3">
              <button
                className="w-full rounded-lg bg-[#FD954F] px-4 py-3 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
                onClick={onChooseTrial}
                ref={primaryActionRef}
                type="button"
              >
                Start 3-day free trial and keep speaking
              </button>
              <p className="text-center text-xs text-neutral-400">
                Create an account first. Plans and renewal terms are shown before checkout.
              </p>
              <button
                className="w-full rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
                onClick={onChooseFree}
                type="button"
              >
                Sign up free and continue for 5 more minutes
              </button>
              <button
                className="w-full rounded-lg px-4 py-2 text-sm font-medium text-neutral-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={() => onDismiss("not_now")}
                type="button"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
